import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { formatMoney } from '../utils/currency';
import { divisionTransfers, businesses as businessesApi } from '../api';
import { ArrowRightLeft, Trash2, Info } from 'lucide-react';

const DivisionTransfers = ({ businessId, businessName, businessCurrency }) => {
  const { toast } = useToast();
  const [units, setUnits] = useState([]);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ from_business_id: '', to_business_id: '', amount: '', date: new Date().toISOString().slice(0, 10), memo: '' });

  useEffect(() => {
    if (!businessId) return;
    (async () => {
      try {
        const res = await businessesApi.getAll();
        const all = res.data || [];
        const parent = all.find((b) => b.id === businessId);
        if (parent) {
          const subs = all.filter((b) => (parent.subsidiary_ids || []).includes(b.id));
          setUnits([{ id: parent.id, name: 'Corporate / Head Office', currency: parent.currency || 'USD' }, ...subs]);
        }
      } catch (e) { /* ignore */ }
      setUnitsLoaded(true);
      fetchList();
    })();
  }, [businessId]);

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await divisionTransfers.list(businessId);
      setList(res.data || []);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed to load transfers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fromUnit = units.find((u) => u.id === form.from_business_id);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await divisionTransfers.create({ business_id: businessId, ...form, amount: parseFloat(form.amount) });
      toast({ title: 'Transfer recorded', description: 'Cash moved between divisions. No revenue or expense was created.' });
      setForm((f) => ({ ...f, amount: '', memo: '' }));
      fetchList();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Failed to record transfer', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this transfer? Both divisions\' cash balances will be restored.')) return;
    try {
      await divisionTransfers.delete(id);
      toast({ title: 'Transfer deleted' });
      fetchList();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete transfer', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6" data-testid="division-transfers">
      <Card className="border-l-4 border-l-blue-600 bg-blue-50/40">
        <CardContent className="pt-4 flex gap-3 text-sm text-blue-900">
          <Info className="h-5 w-5 shrink-0 text-blue-600" />
          <p>Internal transfers move cash between divisions of <strong>{businessName}</strong>. They change each division's cash balance and cash flow but are <strong>never</strong> counted as revenue or expense, and they net to zero in the legal entity's consolidated reports.</p>
        </CardContent>
      </Card>

      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><ArrowRightLeft className="h-5 w-5 text-blue-600" /> New Transfer</CardTitle>
        </CardHeader>
        <CardContent>
          {!unitsLoaded ? (
            <p className="text-sm text-gray-500">Loading divisions…</p>
          ) : units.length < 2 ? (
            <p className="text-sm text-gray-500">Add at least one operating division to this legal entity to record transfers.</p>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <Label>From</Label>
                <Select value={form.from_business_id} onValueChange={(v) => setForm({ ...form, from_business_id: v })}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Source division" /></SelectTrigger>
                  <SelectContent>
                    {units.filter((u) => u.id !== form.to_business_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To</Label>
                <Select value={form.to_business_id} onValueChange={(v) => setForm({ ...form, to_business_id: v })}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Destination division" /></SelectTrigger>
                  <SelectContent>
                    {units.filter((u) => u.id !== form.from_business_id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount {fromUnit ? `(${fromUnit.currency})` : ''}</Label>
                <Input type="number" step="0.01" min="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="transfer-amount-input" />
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="transfer-date-input" />
              </div>
              <div className="md:col-span-4">
                <Label>Memo</Label>
                <Input value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="e.g. Working capital top-up for Q3" data-testid="transfer-memo-input" />
              </div>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={!form.from_business_id || !form.to_business_id} data-testid="transfer-submit-btn">
                Record Transfer
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-md">
        <CardHeader><CardTitle className="text-lg">Transfer History</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="transfers-empty">No transfers recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transfers-table">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">From</th>
                    <th className="text-left px-3 py-2">To</th>
                    <th className="text-left px-3 py-2">Memo</th>
                    <th className="text-right px-3 py-2">Amount</th>
                    <th className="text-right px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {list.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50" data-testid={`transfer-row-${t.id}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{t.date}</td>
                      <td className="px-3 py-2">{t.from_name}</td>
                      <td className="px-3 py-2">{t.to_name}</td>
                      <td className="px-3 py-2 text-gray-600">{t.memo || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono whitespace-nowrap">
                        {formatMoney(t.amount, t.from_currency)}
                        {t.from_currency !== t.to_currency && <span className="block text-[11px] text-gray-500">≈ {formatMoney(t.to_amount, t.to_currency)}</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50 h-7" onClick={() => remove(t.id)} data-testid={`delete-transfer-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DivisionTransfers;
